import React, { startTransition } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ChatSidebarScopeTab } from 'utils/chat-sidebar-unified-statuses';
import { indexFromScopeTab } from 'utils/chat-sidebar-unified-statuses';
import type { ActiveTabId, PerScopeStatusVisibility, SidebarStatusVisibility } from 'utils/chat-sidebar-layout-storage';
import {
  loadActiveTabId,
  loadLastBuiltinScope,
  loadPerScopeStatusVisibility,
  saveLastBuiltinScope,
  savePerScopeStatusVisibility,
} from 'utils/chat-sidebar-layout-storage';
import { useSelector } from 'store';
import { appendViewQueryToPath } from 'features/Chat/chat-view-query';
import { appendChatUserToChatViewUrl } from 'utils/chat-workflow-url';
import { deriveActiveTabIdFromUrl } from 'features/Chat/derive-chat-sidebar-tab';
import {
  generateFolderId,
  loadCustomFolders,
  loadViewFolderMap,
  saveCustomFolders,
  saveViewFolderMap,
  BUILTIN_CUSTOM_VIEWS_FOLDER_ID,
  BUILTIN_UNASSIGNED_FOLDER_ID,
  type ChatSidebarFolder,
  type ViewFolderMap,
} from 'utils/chat-sidebar-folders-storage';
import { persistSidebarLayoutToServer } from 'utils/chat-sidebar-server-sync';

type Ctx = {
  /** נגזר מ-URL בלבד — `view` query + נתיב */
  activeTabId: ActiveTabId;
  /** ניווט לטאב — מעדכן URL (מקור האמת) */
  setActiveTabId: (id: ActiveTabId) => void;
  scope: ChatSidebarScopeTab;
  tabIndex: number;
  setTabIndex: (index: number) => void;
  statusVisibility: SidebarStatusVisibility;
  /** נראות שורות לפי טווח (שלי / צוות / כל השיחות) */
  perScopeStatusVisibility: PerScopeStatusVisibility;
  patchStatusVisibility: (patch: Partial<SidebarStatusVisibility['statusRows']>, targetScope?: ChatSidebarScopeTab) => void;
  manageViewsOpen: boolean;
  setManageViewsOpen: (open: boolean) => void;
  showGlobalQuickFiltersBar: boolean;
  //* תיקיות סרגל-הצד — נשמרות בשרת (userConfig.chatSidebarLayout); localStorage = seed מהיר
  /** תיקיות שהמשתמש יצר (מעבר ל-3 המובנות: mine/team/all) */
  customFolders: ChatSidebarFolder[];
  /** מיפוי תצוגה→תיקייה */
  viewFolderMap: ViewFolderMap;
  /** הוספת תיקייה חדשה — מחזיר את ה-id שנוצר */
  addFolder: (name: string) => string;
  renameFolder: (id: string, name: string) => void;
  /** מחיקת תיקייה — תצוגות ששויכו אליה חוזרות ל"ללא שיוך" */
  removeFolder: (id: string) => void;
  /** שיוך תצוגה לתיקייה */
  assignViewToFolder: (viewId: string, folderId: string) => void;
  /** תיקיות פתוחות בסרגל (מותר יותר מאחת) */
  isFolderExpanded: (folderId: string) => boolean;
  setFolderExpanded: (folderId: string, expanded: boolean) => void;
};

const ChatSidebarLayoutContext = React.createContext<Ctx | null>(null);

export function ChatSidebarLayoutProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedChatPhone = useSelector((s) => s.chat.selectedChatUser?.UserDisplayPhoneNumber);

  const [lastBuiltinScope, setLastBuiltinScope] = React.useState<ChatSidebarScopeTab>(() => loadLastBuiltinScope());
  const [perScopeStatusVisibility, setPerScopeStatusVisibility] = React.useState(() =>
    loadPerScopeStatusVisibility(),
  );
  const [manageViewsOpen, setManageViewsOpen] = React.useState(false);

  //* תיקיות + שיוך תצוגות — מקור האמת בשרת (userConfig.chatSidebarLayout);
  //* ה-localStorage נשאר כ-seed מהיר לעלייה וכמקור מיגרציה חד-פעמית.
  const [customFolders, setCustomFolders] = React.useState<ChatSidebarFolder[]>(() => loadCustomFolders());
  const [viewFolderMap, setViewFolderMap] = React.useState<ViewFolderMap>(() => loadViewFolderMap());

  //* אימוץ חד-פעמי מהשרת (server wins) או מיגרציה של הלוקאל אם השרת ריק.
  //* mutatedRef מגן על שינויים שהמשתמש עשה לפני שהשרת הספיק לענות.
  const serverLayout = useSelector((s) => s.userConfig.chatSidebarLayout);
  const configLoaded = useSelector((s) => s.userConfig.userConfigLoadedFromServer);
  const adoptedRef = React.useRef(false);
  const mutatedRef = React.useRef(false);
  React.useEffect(() => {
    if (adoptedRef.current || !configLoaded) return;
    adoptedRef.current = true;
    if (serverLayout && (serverLayout.perScopeStatusVisibility || serverLayout.customFolders || serverLayout.viewFolderMap)) {
      if (mutatedRef.current) return;
      if (serverLayout.perScopeStatusVisibility) {
        setPerScopeStatusVisibility((prev) => {
          const merged = { ...prev } as typeof prev;
          for (const scopeKey of ['mine', 'team', 'all'] as const) {
            const rows = serverLayout.perScopeStatusVisibility?.[scopeKey]?.statusRows;
            if (rows) merged[scopeKey] = { statusRows: { ...prev[scopeKey].statusRows, ...rows } };
          }
          savePerScopeStatusVisibility(merged);
          return merged;
        });
      }
      if (serverLayout.customFolders) {
        setCustomFolders(serverLayout.customFolders);
        saveCustomFolders(serverLayout.customFolders);
      }
      if (serverLayout.viewFolderMap) {
        setViewFolderMap(serverLayout.viewFolderMap);
        saveViewFolderMap(serverLayout.viewFolderMap);
      }
    } else {
      // מיגרציה חד-פעמית: לשרת אין עדיין מבנה סרגל — מעלים את מה שיש בלוקאל
      persistSidebarLayoutToServer({
        perScopeStatusVisibility,
        customFolders,
        viewFolderMap,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoaded, serverLayout]);
  const [expandedFolderIds, setExpandedFolderIds] = React.useState<Set<string>>(
    () => new Set([loadLastBuiltinScope()]),
  );

  const isFolderExpanded = React.useCallback(
    (folderId: string) => expandedFolderIds.has(folderId),
    [expandedFolderIds],
  );

  const setFolderExpanded = React.useCallback((folderId: string, expanded: boolean) => {
    setExpandedFolderIds((prev) => {
      const has = prev.has(folderId);
      if (expanded === has) return prev;
      const next = new Set(prev);
      if (expanded) next.add(folderId);
      else next.delete(folderId);
      return next;
    });
  }, []);

  const ensureFolderExpanded = React.useCallback((folderId: string) => {
    setExpandedFolderIds((prev) => {
      if (prev.has(folderId)) return prev;
      const next = new Set(prev);
      next.add(folderId);
      return next;
    });
  }, []);

  const addFolder = React.useCallback((name: string) => {
    const id = generateFolderId();
    mutatedRef.current = true;
    setCustomFolders((prev) => {
      const next = [...prev, { id, name: name.trim() || 'תיקייה' }];
      saveCustomFolders(next);
      persistSidebarLayoutToServer({ customFolders: next });
      return next;
    });
    ensureFolderExpanded(id);
    return id;
  }, [ensureFolderExpanded]);

  const renameFolder = React.useCallback((id: string, name: string) => {
    mutatedRef.current = true;
    setCustomFolders((prev) => {
      const next = prev.map((f) => (f.id === id ? { ...f, name: name.trim() || f.name } : f));
      saveCustomFolders(next);
      persistSidebarLayoutToServer({ customFolders: next });
      return next;
    });
  }, []);

  const removeFolder = React.useCallback((id: string) => {
    mutatedRef.current = true;
    setCustomFolders((prev) => {
      const next = prev.filter((f) => f.id !== id);
      saveCustomFolders(next);
      persistSidebarLayoutToServer({ customFolders: next });
      return next;
    });
    // תצוגות ששויכו לתיקייה שנמחקה — מסירים את השיוך כדי שיחזרו ל"ללא שיוך"
    setViewFolderMap((prev) => {
      let changed = false;
      const next: ViewFolderMap = {};
      for (const [viewId, folderId] of Object.entries(prev)) {
        if (folderId === id) {
          changed = true;
          continue;
        }
        next[viewId] = folderId;
      }
      if (changed) {
        saveViewFolderMap(next);
        persistSidebarLayoutToServer({ viewFolderMap: next });
      }
      return changed ? next : prev;
    });
  }, []);

  const assignViewToFolder = React.useCallback((viewId: string, folderId: string) => {
    mutatedRef.current = true;
    setViewFolderMap((prev) => {
      const next = { ...prev, [viewId]: folderId };
      saveViewFolderMap(next);
      persistSidebarLayoutToServer({ viewFolderMap: next });
      return next;
    });
  }, []);

  const [fallbackWhenNotChat] = React.useState<ActiveTabId>(() => loadActiveTabId());

  const activeTabId = React.useMemo(
    () => deriveActiveTabIdFromUrl(location.pathname, location.search, fallbackWhenNotChat),
    [location.pathname, location.search, fallbackWhenNotChat],
  );

  React.useEffect(() => {
    if (activeTabId === 'mine' || activeTabId === 'team' || activeTabId === 'all') {
      setLastBuiltinScope(activeTabId);
      saveLastBuiltinScope(activeTabId);
    }
  }, [activeTabId]);

  const setActiveTabId = React.useCallback(
    (id: ActiveTabId) => {
      let target = '';
      if (id === 'mine') {
        target = appendViewQueryToPath(appendChatUserToChatViewUrl('/chats/my', selectedChatPhone), 'mine');
      } else if (id === 'team') {
        target = appendViewQueryToPath(appendChatUserToChatViewUrl('/chats/not-my', selectedChatPhone), 'team');
      } else if (id === 'all') {
        target = appendViewQueryToPath(appendChatUserToChatViewUrl('/chats', selectedChatPhone), 'all');
      } else if (id.startsWith('custom:')) {
        const cid = id.slice('custom:'.length);
        target = appendViewQueryToPath(
          appendChatUserToChatViewUrl(`/chats/custom/${cid}`, selectedChatPhone),
          id,
        );
      }
      if (target) {
        startTransition(() => {
          navigate(target);
        });
      }
    },
    [navigate, selectedChatPhone],
  );

  const scope: ChatSidebarScopeTab = React.useMemo(() => {
    if (activeTabId === 'mine' || activeTabId === 'team' || activeTabId === 'all') {
      return activeTabId;
    }
    return lastBuiltinScope;
  }, [activeTabId, lastBuiltinScope]);

  // סנכרון התיקייה הפתוחה עם התצוגה/הטווח הפעיל:
  // טאב מובנה → פותחים את התיקייה המובנה התואמת; תצוגה מותאמת → פותחים את התיקייה ששויכה לה.
  React.useEffect(() => {
    if (location.pathname.startsWith('/chats/unassigned')) {
      ensureFolderExpanded(BUILTIN_UNASSIGNED_FOLDER_ID);
      return;
    }
    if (activeTabId === 'mine' || activeTabId === 'all') {
      ensureFolderExpanded(activeTabId);
      return;
    }
    if (activeTabId === 'team') {
      ensureFolderExpanded('all');
      return;
    }
    if (activeTabId.startsWith('custom:')) {
      const viewId = activeTabId.slice('custom:'.length);
      //* תצוגה בלי שיוך תיקייה שייכת לתיקיית המערכת «מותאמות» (לא ל«שלי»)
      ensureFolderExpanded(viewFolderMap[viewId] ?? BUILTIN_CUSTOM_VIEWS_FOLDER_ID);
    }
  }, [activeTabId, viewFolderMap, location.pathname, ensureFolderExpanded]);

  const statusVisibility = React.useMemo(
    () => perScopeStatusVisibility[scope],
    [perScopeStatusVisibility, scope],
  );

  const patchStatusVisibility = React.useCallback(
    (patch: Partial<SidebarStatusVisibility['statusRows']>, targetScope?: ChatSidebarScopeTab) => {
      const effectiveScope = targetScope ?? scope;
      mutatedRef.current = true;
      setPerScopeStatusVisibility((prev) => {
        const cur = prev[effectiveScope];
        const nextScope: SidebarStatusVisibility = {
          statusRows: { ...cur.statusRows, ...patch } as Record<string, boolean>,
        };
        const next = { ...prev, [effectiveScope]: nextScope };
        savePerScopeStatusVisibility(next);
        persistSidebarLayoutToServer({ perScopeStatusVisibility: next });
        return next;
      });
    },
    [scope],
  );

  const tabIndex = React.useMemo(() => indexFromScopeTab(scope), [scope]);

  const setTabIndex = React.useCallback(
    (index: number) => {
      const next = index >= 0 && index <= 2 ? index : 0;
      const id = (['mine', 'team', 'all'] as const)[next] ?? 'mine';
      setActiveTabId(id);
    },
    [setActiveTabId],
  );

  const showGlobalQuickFiltersBar = location.pathname.startsWith('/chats') && activeTabId === 'all';

  const value = React.useMemo(
    () => ({
      activeTabId,
      setActiveTabId,
      scope,
      tabIndex,
      setTabIndex,
      statusVisibility,
      perScopeStatusVisibility,
      patchStatusVisibility,
      manageViewsOpen,
      setManageViewsOpen,
      showGlobalQuickFiltersBar,
      customFolders,
      viewFolderMap,
      addFolder,
      renameFolder,
      removeFolder,
      assignViewToFolder,
      isFolderExpanded,
      setFolderExpanded,
    }),
    [
      activeTabId,
      setActiveTabId,
      scope,
      tabIndex,
      setTabIndex,
      statusVisibility,
      perScopeStatusVisibility,
      patchStatusVisibility,
      manageViewsOpen,
      showGlobalQuickFiltersBar,
      customFolders,
      viewFolderMap,
      addFolder,
      renameFolder,
      removeFolder,
      assignViewToFolder,
      isFolderExpanded,
      setFolderExpanded,
    ],
  );

  return <ChatSidebarLayoutContext.Provider value={value}>{children}</ChatSidebarLayoutContext.Provider>;
}

export function useChatSidebarLayout(): Ctx {
  const ctx = React.useContext(ChatSidebarLayoutContext);
  if (!ctx) throw new Error('useChatSidebarLayout must be used within ChatSidebarLayoutProvider');
  return ctx;
}

export function useChatSidebarLayoutOptional(): Ctx | null {
  return React.useContext(ChatSidebarLayoutContext);
}

export function useChatSidebarScopeTabCompat(): Pick<Ctx, 'tabIndex' | 'scope' | 'setTabIndex'> {
  const { tabIndex, scope, setTabIndex } = useChatSidebarLayout();
  return { tabIndex, scope, setTabIndex };
}

export type { ActiveTabId, SidebarStatusVisibility };
